<template>
  <table class="table-auto w-full bg-white rounded-md">
    <tbody>
      <tr
        v-for="(item, index) in settingTiles"
        :key="index"
        class="hover:bg-gray-100 transition duration-200 flex flex-col md:flex-row border-b border-gray-100 last:border-b-0"
      >
        <td
          class="md:pl-4 sm:pl-0 md:py-3 sm:py-2 pt-4 text-start w-full sm:w-1/3"
        >
          <a
            class="ml-2 flex items-center"
            v-if="!item.onClick"
          >
            <i :class="`${item.icon} ${item.color} text-xl`"></i>
            <span class="ml-2 text-xl">
              {{ item.label }}
            </span>
          </a>
        </td>
        <td
          class="py-2 md:mt-3 md:ml-3 sm:py-0 text-start text-gray-400 pl-4 sm:pl-0 pb-3 sm:pb-2 w-full"
          :class="[item.color, { 'md:col-span-2': item.onClick }]"
        >
          <Button
            v-if="item.onClick"
            :label="item.label"
            :icon="item.icon"
            :severity="item.severity || 'primary'"
            @click="item.onClick"
            class="w-full justify-start"
          />
          <div
            v-else
            v-html="item.value"
            class="text-lg"
          ></div>
        </td>
      </tr>
    </tbody>
  </table>
  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
</template>

<script setup>
  import Helper from "../../utils/helper";
  import { ref, computed } from "vue";
  import { useUserStore } from "@/stores/userStore";
  import { useRxDBStore } from "@/stores/rxDBStore";
  import Constants from "@/utils/constants";
  import { useRouter, useRoute } from "vue-router";
  import Auth from "@/utils/auth";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();
  const rxDBStore = useRxDBStore();
  const errorObj = ref({
    isVisible: false,
    errorMsg: ""
  });
  const onErrorDialogClose = () => {
    errorObj.value.isVisible = false;
  };
  const refreshSession = async () => {
    const _success = await Auth.refreshSession();
    if (_success) {
      Helper.showToast("Success", "Session refreshed successfully", "success");
      errorObj.value.isVisible = false;
    } else {
      errorObj.value.isVisible = true;
      errorObj.value.errorMsg = "Failed to refresh session";
    }
  };
  const clearCache = async () => {
    Helper.handleRouteNavigation(
      router,
      route,
      Constants.enums.ROUTE_ENUM.HOME
    );
    Helper.clearAllSiteData();
  };
  const settingTiles = ref([
    {
      label: "App Version",
      value: `v${Helper.getCurrentVersion()}`,
      icon: "pi pi-hashtag"
    },
    {
      label: "Last Synced At", 
      value: computed(() =>
        rxDBStore.getSyncedAt
          ? Helper.convertToLocalDate(
              rxDBStore.getSyncedAt,
              "DD/MM/YYYY hh:mm A"
            )
          : "Not Synced"
      ),
      icon: "pi pi-sync"
    },
    {
      label: "Internet Connection",
      value: computed(() =>
        userStore.isOnline ? "Connected" : "Disconnected"
      ),
      icon: "pi pi-wifi",
      color: computed(() =>
        userStore.isOnline ? "text-green-800" : "text-red-800"
      )
    },
    {
      label: "Session Expiry",
      value: computed(() =>
        Helper.isSessionExpiringSoon().isExpired
          ? `${Helper.isSessionExpiringSoon().local} (Expired)`
          : Helper.isSessionExpiringSoon().isExpiringSoon
            ? `${Helper.isSessionExpiringSoon().local} (Expiring Soon) | ${Helper.getTokenAge()} hours left`
            : `${Helper.isSessionExpiringSoon().local} (Active) | ${Helper.getTokenAge()} hours left`
      ),
      icon: "pi pi-circle-fill",
      color: computed(() =>
        Helper.isSessionExpiringSoon().isExpired
          ? "text-red-800"
          : Helper.isSessionExpiringSoon().isExpiringSoon
            ? "text-yellow-800"
            : "text-green-800"
      )
    },
    ...(userStore.isUserSignedIn() ? [{
      label: "Update Session",
      value: "",
      icon: "pi pi-refresh", 
      onClick: refreshSession,
      severity: "secondary"
    }] : []),
    {
      label: "Clear Cache",
      value: "",
      icon: "pi pi-trash",
      onClick: clearCache,
      severity: "contrast"
    }
  ]);
</script>
